/**
 * Toast notification utility wrapper for react-hot-toast
 * Provides a consistent interface for displaying notifications throughout the app
 *
 * @module toast
 */

import toast from 'react-hot-toast';

/**
 * Toast notification options
 */
export interface ToastOptions {
  /** Description or additional details to show below the main message */
  description?: string;
  /** Duration in milliseconds (default: 3000 for success, 5000 for errors) */
  duration?: number;
  /** Position on screen */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** Custom icon to display (emoji or text character) */
  icon?: string;
}

/**
 * Show a success toast notification
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const showSuccess = (message: string, options: ToastOptions = {}) => {
  const { description, duration = 3000, position = 'top-right', icon = '✓' } = options;

  const content = description
    ? (
      <div>
        <div className="font-semibold">{message}</div>
        <div className="text-sm opacity-90">{description}</div>
      </div>
    )
    : message;

  return toast.success(content, {
    duration,
    position,
    icon,
    style: {
      background: '#10b981',
      color: '#fff',
      padding: '16px',
      borderRadius: '8px',
      maxWidth: '500px',
    },
  });
};

/**
 * Show an error toast notification
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const showError = (message: string, options: ToastOptions = {}) => {
  const { description, duration = 5000, position = 'top-right', icon = '✕' } = options;

  const content = description
    ? (
      <div>
        <div className="font-semibold">{message}</div>
        <div className="text-sm opacity-90">{description}</div>
      </div>
    )
    : message;

  return toast.error(content, {
    duration,
    position,
    icon,
    style: {
      background: '#ef4444',
      color: '#fff',
      padding: '16px',
      borderRadius: '8px',
      maxWidth: '500px',
    },
  });
};

/**
 * Show a warning toast notification
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const showWarning = (message: string, options: ToastOptions = {}) => {
  const { description, duration = 4000, position = 'top-right', icon = '⚠' } = options;

  const content = description
    ? (
      <div>
        <div className="font-semibold">{message}</div>
        <div className="text-sm opacity-90">{description}</div>
      </div>
    )
    : message;

  return toast(content, {
    duration,
    position,
    icon,
    style: {
      background: '#f59e0b',
      color: '#fff',
      padding: '16px',
      borderRadius: '8px',
      maxWidth: '500px',
    },
  });
};

/**
 * Show an info toast notification
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const showInfo = (message: string, options: ToastOptions = {}) => {
  const { description, duration = 3000, position = 'top-right', icon = 'ℹ' } = options;

  const content = description
    ? (
      <div>
        <div className="font-semibold">{message}</div>
        <div className="text-sm opacity-90">{description}</div>
      </div>
    )
    : message;

  return toast(content, {
    duration,
    position,
    icon,
    style: {
      background: '#3b82f6',
      color: '#fff',
      padding: '16px',
      borderRadius: '8px',
      maxWidth: '500px',
    },
  });
};

/**
 * Show a loading toast notification
 * Returns a function to dismiss the toast
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const showLoading = (message: string, options: ToastOptions = {}) => {
  const { position = 'top-right' } = options;

  const toastId = toast.loading(message, {
    position,
    style: {
      background: '#6b7280',
      color: '#fff',
      padding: '16px',
      borderRadius: '8px',
      maxWidth: '500px',
    },
  });

  return () => toast.dismiss(toastId);
};

/**
 * Show a confirmation dialog using toast
 * This is a non-blocking alternative to window.confirm()
 * @param message - Main message to display
 * @param options - Configuration with onConfirm and onCancel callbacks
 */
export const showConfirm = (
  message: string,
  options: {
    description?: string;
    onConfirm: () => void;
    onCancel?: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    duration?: number;
  }
) => {
  const {
    description,
    onConfirm,
    onCancel,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    duration = 10000,
  } = options;

  return toast(
    (t) => (
      <div className="flex flex-col gap-3">
        <div>
          <div className="font-semibold">{message}</div>
          {description && <div className="text-sm opacity-90 mt-1">{description}</div>}
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              onCancel?.();
            }}
            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              onConfirm();
            }}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    ),
    {
      duration,
      position: 'top-center',
      icon: '❓',
      style: {
        background: '#fff',
        color: '#1f2937',
        padding: '16px',
        borderRadius: '8px',
        maxWidth: '500px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      },
    }
  );
};

/**
 * Promise-based confirmation dialog
 * Returns a promise that resolves to true if confirmed, false if cancelled
 * @param message - Main message to display
 * @param options - Optional configuration
 */
export const confirmAsync = (
  message: string,
  options: {
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
  } = {}
): Promise<boolean> => {
  return new Promise((resolve) => {
    showConfirm(message, {
      ...options,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

/**
 * Dismiss a specific toast by ID
 * @param toastId - ID of the toast to dismiss
 */
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all active toasts
 */
export const dismissAll = () => {
  toast.dismiss();
};

// Re-export the base toast function for advanced use cases
export { toast };
